import React from "react";

/**
 * Control Room section icons.
 *
 * One flat set so every heading reads as part of the same family: 24x24 grid,
 * stroked in currentColor (never filled), 1.7 stroke, round caps and joins.
 * They inherit colour from the heading they sit in, so light and dark themes
 * need no separate values.
 *
 * Decorative only — every icon sits beside its own text label, so they are
 * aria-hidden and add nothing for a screen reader to read twice.
 */
const PATHS = {
  // sections
  schedule: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7.5V12l3 2" />
    </>
  ),
  decision: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <circle cx="12" cy="12" r="4" />
      <circle cx="12" cy="12" r="0.6" fill="currentColor" stroke="none" />
    </>
  ),
  connections: (
    <>
      <path d="M10.5 13.5a4 4 0 0 0 5.7 0l2.3-2.3a4 4 0 0 0-5.7-5.7l-1.2 1.2" />
      <path d="M13.5 10.5a4 4 0 0 0-5.7 0l-2.3 2.3a4 4 0 0 0 5.7 5.7l1.2-1.2" />
    </>
  ),
  context: (
    <>
      <path d="M6 4.5h9l3.5 3.5v11.5H6z" />
      <path d="M14.5 4.5V8H18" />
      <path d="M9 12.5h6M9 16h4" />
    </>
  ),
  display: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 3.5v17" />
      <path d="M12 8.5a3.5 3.5 0 0 1 0 7" fill="currentColor" stroke="none" />
    </>
  ),

  // cards
  automations: <path d="M13.5 3 5.5 13.5H11l-.5 7.5 8-10.5H13z" />,
  automation: (
    <>
      <circle cx="12" cy="12" r="3.2" />
      <path d="M12 3v2.4M12 18.6V21M21 12h-2.4M5.4 12H3M18.4 5.6l-1.7 1.7M7.3 16.7l-1.7 1.7M18.4 18.4l-1.7-1.7M7.3 7.3 5.6 5.6" />
    </>
  ),
  tuning: (
    <>
      <path d="M4 8h9M17.5 8H20M4 16h3.5M12 16h8" />
      <circle cx="15" cy="8" r="2.2" />
      <circle cx="9.5" cy="16" r="2.2" />
    </>
  ),
  ai: (
    <>
      <path d="M12 4l1.4 3.9L17.5 9.3l-3.9 1.4L12 14.6l-1.4-3.9L6.5 9.3l4.1-1.4z" />
      <path d="M18 15.5l.7 1.9 1.9.7-1.9.7-.7 1.9-.7-1.9-1.9-.7 1.9-.7z" />
    </>
  ),
  scope: (
    <>
      <path d="M6 20V4.5l1.2.6a7 7 0 0 0 6.3 0l.6-.3a7 7 0 0 1 6.3 0v9a7 7 0 0 0-6.3 0l-.6.3a7 7 0 0 1-6.3 0L6 13.5" />
    </>
  ),
  tracking: (
    <>
      <path d="M2.8 12S6.6 6 12 6s9.2 6 9.2 6-3.8 6-9.2 6-9.2-6-9.2-6z" />
      <circle cx="12" cy="12" r="2.6" />
    </>
  ),
  inbox: (
    <>
      <path d="M3.5 13.5 6 5.5h12l2.5 8v5h-17z" />
      <path d="M3.5 13.5H9a3 3 0 0 0 6 0h5.5" />
    </>
  ),

  // automations
  pings: (
    <>
      <path d="M18 16.5v-4.8a6 6 0 1 0-12 0v4.8L4.5 19h15z" />
      <path d="M10 21.5a2.4 2.4 0 0 0 4 0" />
    </>
  ),
  meetings: (
    <>
      <rect x="3.5" y="5.5" width="17" height="15" rx="2.5" />
      <path d="M8 3.2v4.2M16 3.2v4.2M3.5 10.5h17" />
    </>
  ),
  tasks: (
    <>
      <path d="M4 7h8M4 12h8M4 17h5" />
      <path d="m14.5 15.5 2.2 2.2L21 13.5" />
    </>
  ),
  escalations: (
    <>
      <path d="M12 3.6 2.8 20h18.4z" />
      <path d="M12 9.8v4.4" />
      <circle cx="12" cy="17.2" r="0.7" fill="currentColor" stroke="none" />
    </>
  ),
  email: (
    <>
      <rect x="3" y="5.8" width="18" height="12.4" rx="2.5" />
      <path d="m3.8 7.6 7.1 4.8a2 2 0 0 0 2.2 0l7.1-4.8" />
    </>
  ),
  report: (
    <>
      <path d="M6 3.5h8l4 4v13H6z" />
      <path d="M14 3.5V8h4" />
      <path d="M9 17v-3M12 17v-5.5M15 17v-2" />
    </>
  ),
  team: (
    <>
      <circle cx="9.5" cy="8.5" r="3.2" />
      <path d="M3.5 19.5a6 6 0 0 1 12 0" />
      <path d="M16 6.2a3.2 3.2 0 0 1 0 5.9" />
      <path d="M17.5 14.4a6 6 0 0 1 3 5.1" />
    </>
  ),

  // account (pages/Account.js)
  person: (
    <>
      <circle cx="12" cy="8.2" r="3.6" />
      <path d="M5 20a7 7 0 0 1 14 0" />
    </>
  ),
  lock: (
    <>
      <rect x="4.8" y="10.2" width="14.4" height="9.6" rx="2.6" />
      <path d="M8.4 10.2V7.8a3.6 3.6 0 0 1 7.2 0v2.4" />
      <path d="M12 14v2.2" />
    </>
  ),
  shield: (
    <>
      <path d="M12 3.4 5 6v5.6c0 4.2 2.9 7.4 7 9 4.1-1.6 7-4.8 7-9V6z" />
      <path d="m9.2 12.2 2 2 3.6-3.8" />
    </>
  ),
  devices: (
    <>
      <rect x="2.8" y="5.5" width="12.4" height="9" rx="2" />
      <path d="M6.6 18.5h6" />
      <path d="M9.8 14.5v4" />
      <rect x="17.2" y="9.5" width="4" height="9" rx="1.6" />
    </>
  ),
};

export default function CrIcon({ name, size = 16, className = "" }) {
  const glyph = PATHS[name];
  if (!glyph) return null;
  return (
    <svg
      className={`crIcon ${className}`.trim()}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {glyph}
    </svg>
  );
}
